import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/api/client';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '@/components/ui/alert-dialog';
import { UserPlus, Loader2, CheckCircle, BadgeCheck } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';

export default function Rewards() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [selectedContact, setSelectedContact] = useState(null);
  const [showSuccessOverlay, setShowSuccessOverlay] = useState(false);
  const [showDisableConfirm, setShowDisableConfirm] = useState(false);
  const [fieldDefinitionsCache, setFieldDefinitionsCache] = useState({});
  const [loadingFieldDefinitions, setLoadingFieldDefinitions] = useState(false);
  const [rewardsSettings, setRewardsSettings] = useState({
    checkboxFieldId: '',
    checkboxOptionValue: 'Yes',
    dateFieldId: '',
    folderId: ''
  });
  const [contactForm, setContactForm] = useState({ 
    firstName: '', 
    lastName: '', 
    phone: '', 
    email: '',
    address1: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  });
  const [form, setForm] = useState({ 
    firstName: '', 
    lastName: '', 
    phone: '', 
    email: '', 
    address1: '',
    city: '',
    state: '',
    country: '',
    postalCode: ''
  });
  const { toast } = useToast();

  // Fetch field definitions when a contact is selected
  useEffect(() => {
    if (selectedContact) {
      const customFields = selectedContact.customField || selectedContact.customFields || [];
      
      console.log('[useEffect] Fetching field definitions for contact:', selectedContact.id);
      console.log('[useEffect] Custom fields to fetch:', customFields);
      console.log('[useEffect] Current cache:', fieldDefinitionsCache);
      
      // Check if we need to fetch any fields
      const fieldsToFetch = customFields.filter(field => {
        const fieldId = field.id || field.key;
        return fieldId && !fieldDefinitionsCache[fieldId];
      });
      
      if (fieldsToFetch.length > 0) {
        console.log('[useEffect] Need to fetch', fieldsToFetch.length, 'field definitions');
        setLoadingFieldDefinitions(true);
        
        // Fetch definitions for all custom fields
        const fetchPromises = fieldsToFetch.map(field => {
          const fieldId = field.id || field.key;
          console.log('[useEffect] Fetching definition for field:', fieldId);
          return fetchFieldDefinition(fieldId);
        });
        
        // Wait for all fetches to complete
        Promise.all(fetchPromises).then(() => {
          console.log('[useEffect] All field definitions fetched');
          setLoadingFieldDefinitions(false);
        }).catch(error => {
          console.error('[useEffect] Error fetching field definitions:', error);
          setLoadingFieldDefinitions(false);
        });
      } else {
        console.log('[useEffect] All fields already cached or no fields to fetch');
      }
    }
  }, [selectedContact]);

  // Debounced search function
  const searchContacts = useCallback(async (searchQuery) => {
    if (!searchQuery || searchQuery.length < 2) {
      setSearchResults([]);
      setSearching(false);
      return;
    }

    setSearching(true);
    try {
      const { data } = await api.post('/live/contacts/search', 
        { query: searchQuery },
        { headers: { 'X-Use-Live': 'true' } }
      );
      // Limit to top 5 results
      const contacts = data.contacts || [];
      console.log('[Search] Full API response:', data);
      console.log('[Search] Contacts returned:', contacts);
      if (contacts.length > 0) {
        console.log('[Search] First contact structure:', contacts[0]);
        console.log('[Search] First contact customField:', contacts[0].customField);
        console.log('[Search] First contact customFields:', contacts[0].customFields);
      }
      setSearchResults(contacts.slice(0, 5));
    } catch (error) {
      console.error('Search failed:', error);
      setSearchResults([]);
      toast({
        title: 'Search Failed',
        description: 'Failed to search contacts. Please try again.',
        variant: 'destructive'
      });
    } finally {
      setSearching(false);
    }
  }, [toast]);

  // Debounce search - trigger after 300ms of no typing
  useEffect(() => {
    const timer = setTimeout(() => {
      searchContacts(query);
    }, 300);
    return () => clearTimeout(timer);
  }, [query, searchContacts]);

  // Get rewards settings from localStorage
  // Fetch rewards settings from API on mount
  useEffect(() => {
    const fetchRewardsSettings = async () => {
      try {
        const { data } = await api.get('/settings/rewards');
        console.log('[Rewards] Fetched rewards settings:', data);
        setRewardsSettings(data);
      } catch (error) {
        console.error('[Rewards] Failed to fetch rewards settings:', error);
        // Fallback to localStorage
        const s = localStorage.getItem('rewards_settings');
        if (s) {
          setRewardsSettings(JSON.parse(s));
        }
      }
    };
    fetchRewardsSettings();
  }, []);

  const getRewardsSettings = () => {
    return rewardsSettings;
  };

  // Check if contact is already a rewards member
  const isRewardsMember = (contact) => {
    const settings = getRewardsSettings();
    
    if (!settings.checkboxFieldId) {
      console.log('[isRewardsMember] No checkbox field ID configured');
      return false;
    }
    
    // Check if the contact has the rewards custom field activated
    // Try both customField and customFields properties
    const customFields = contact.customField || contact.customFields || [];
    
    console.log('[isRewardsMember] Checking contact:', contact.name || contact.email);
    console.log('[isRewardsMember] Full contact object:', contact);
    console.log('[isRewardsMember] Custom fields array:', customFields);
    console.log('[isRewardsMember] Looking for ID:', settings.checkboxFieldId);
    console.log('[isRewardsMember] Expected value:', settings.checkboxOptionValue);
    
    if (Array.isArray(customFields) && customFields.length > 0) {
      console.log('[isRewardsMember] Field IDs in array:', customFields.map(f => f.id || f.key));
      
      const rewardsField = customFields.find(
        (field) => {
          const fieldId = field.id || field.key || field.fieldKey;
          console.log('[isRewardsMember] Comparing field ID:', fieldId, 'with target:', settings.checkboxFieldId);
          return fieldId === settings.checkboxFieldId;
        }
      );
      
      console.log('[isRewardsMember] Found field:', rewardsField);
      
      if (rewardsField) {
        const value = rewardsField.field_value || rewardsField.value || rewardsField.fieldValue;
        console.log('[isRewardsMember] ==================');
        console.log('[isRewardsMember] Field found! Checking value...');
        console.log('[isRewardsMember] Raw value:', value);
        console.log('[isRewardsMember] Value type:', typeof value);
        console.log('[isRewardsMember] Is Array:', Array.isArray(value));
        console.log('[isRewardsMember] Expected option value:', settings.checkboxOptionValue);
        console.log('[isRewardsMember] ==================');
        
        // Check various formats GHL might return
        let isActive = false;
        
        // Format 1: Array with option value (most common for checkboxes)
        if (Array.isArray(value)) {
          isActive = value.includes(settings.checkboxOptionValue) || value.includes('yes') || value.includes('Yes') || value.includes('YES');
          console.log('[isRewardsMember] Array check result:', isActive, 'Array contents:', value);
        }
        // Format 2: String value
        else if (typeof value === 'string') {
          isActive = value.toLowerCase() === settings.checkboxOptionValue?.toLowerCase() || value.toLowerCase() === 'yes';
          console.log('[isRewardsMember] String check result:', isActive);
        }
        // Format 3: Boolean
        else if (typeof value === 'boolean') {
          isActive = value === true;
          console.log('[isRewardsMember] Boolean check result:', isActive);
        }
        
        console.log('[isRewardsMember] FINAL RESULT:', isActive);
        return isActive;
      }
    } else {
      console.log('[isRewardsMember] No custom fields array or empty array');
    }
    
    console.log('[isRewardsMember] Not a rewards member - field not found or not active');
    return false;
  };

  const handleContactSelect = (contact) => {
    console.log('Selected contact full object:', contact);
    console.log('Custom fields:', contact.customFields);
    
    setSelectedContact(contact);
    setContactForm({
      firstName: contact.firstName || '',
      lastName: contact.lastName || '',
      phone: contact.phone || '',
      email: contact.email || '',
      // Try multiple possible field name variations
      address1: contact.address1 || contact.address || '',
      city: contact.city || '',
      state: contact.state || '',
      country: contact.country || '',
      postalCode: contact.postalCode || contact.postal_code || contact.zip || ''
    });
    setReviewDialogOpen(true);
  };

  // Get Customer Rewards folder custom fields
  const getRewardsCustomFields = (contact) => {
    const customFields = contact.customFields || [];
    const settings = getRewardsSettings();
    
    console.log('=== ALL CUSTOM FIELDS ===');
    customFields.forEach((field, i) => {
      console.log(`Field ${i}:`, {
        id: field.id,
        key: field.key,
        name: field.name,
        parentId: field.parentId,
        value: field.field_value || field.value
      });
    });
    
    console.log('=== FILTER SETTINGS ===', {
      checkboxFieldId: settings.checkboxFieldId,
      folderId: settings.folderId
    });
    
    console.log('=== FIELD DEFINITIONS CACHE ===', fieldDefinitionsCache);
    
    // Filter custom fields based on settings
    const rewardsFields = customFields.filter(field => {
      const id = field.id || '';
      const key = field.key || '';
      
      // Try to get parentId from field data or from cached definition
      let parentId = field.parentId || '';
      if (!parentId && fieldDefinitionsCache[id]) {
        parentId = fieldDefinitionsCache[id].parentId || '';
        console.log(`Got parentId from cache for ${id}: ${parentId}`);
      }
      
      // Exclude the activate checkbox field (it's shown as "Active Member" badge)
      const isActivateField = id === settings.checkboxFieldId || key === settings.checkboxFieldId;
      if (isActivateField) {
        console.log(`Field ${field.name || field.key || field.id}: excluded (activate checkbox)`);
        return false;
      }
      
      // If folder ID is set, only show fields from that folder
      if (settings.folderId && settings.folderId.trim() !== '') {
        const inFolder = parentId === settings.folderId;
        console.log(`Field ${field.name || field.key || field.id}: inFolder=${inFolder} (parentId: ${parentId}, target: ${settings.folderId})`);
        return inFolder;
      }
      
      // If no folder filter, show all fields (except activate checkbox)
      console.log(`Field ${field.name || field.key || field.id}: included (no folder filter)`);
      return true;
    });
    
    console.log('=== FIELDS TO DISPLAY ===', rewardsFields);
    return rewardsFields;
  };

  // Fetch custom field definition by ID
  const fetchFieldDefinition = async (fieldId) => {
    // Check cache first
    if (fieldDefinitionsCache[fieldId]) {
      console.log('[fetchFieldDefinition] Using cached field:', fieldId, fieldDefinitionsCache[fieldId]);
      return fieldDefinitionsCache[fieldId];
    }

    try {
      console.log('[fetchFieldDefinition] Fetching field from API:', fieldId);
      const { data } = await api.get(`/live/custom-fields/${fieldId}`, { 
        params: { live: 1 } 
      });
      
      console.log('[fetchFieldDefinition] API response for field:', fieldId, data);
      
      // Cache it - force a state update to trigger re-render
      setFieldDefinitionsCache(prev => {
        const newCache = {
          ...prev,
          [fieldId]: data
        };
        console.log('[fetchFieldDefinition] Updated cache:', newCache);
        return newCache;
      });
      
      return data;
    } catch (error) {
      console.error('[fetchFieldDefinition] Error fetching field:', fieldId, error);
      console.error('[fetchFieldDefinition] Error details:', error.response?.data);
      return null;
    }
  };

  // Get readable label for custom field (synchronous - for display)
  const getFieldLabel = (field) => {
    const fieldId = field.id || field.key;
    
    console.log('[getFieldLabel] Getting label for field:', {
      fieldId,
      fieldName: field.name,
      fieldKey: field.key,
      cachedDefinition: fieldDefinitionsCache[fieldId],
      allCache: Object.keys(fieldDefinitionsCache)
    });
    
    // Check if we have the definition cached
    if (fieldDefinitionsCache[fieldId]?.name) {
      console.log('[getFieldLabel] Using cached name:', fieldDefinitionsCache[fieldId].name);
      return fieldDefinitionsCache[fieldId].name;
    }
    
    // Use field name if available
    if (field.name) {
      console.log('[getFieldLabel] Using field.name:', field.name);
      return field.name;
    }
    
    // Format the key nicely if available
    if (field.key || field.fieldKey) {
      const key = field.key || field.fieldKey;
      const formatted = key
        .replace(/^contact\./, '')
        .replace(/_/g, ' ')
        .split(' ')
        .map(word => word.charAt(0).toUpperCase() + word.slice(1))
        .join(' ');
      console.log('[getFieldLabel] Using formatted key:', formatted);
      return formatted;
    }
    
    // Last resort: use the ID
    console.log('[getFieldLabel] Using field ID as last resort:', fieldId);
    return field.id || 'Custom Field';
  };

  // Format custom field value for display
  const formatFieldValue = (field) => {
    const value = field.field_value || field.value;
    
    // Handle empty/null values
    if (!value || (Array.isArray(value) && value.length === 0)) {
      return 'Not set';
    }
    
    // Handle array values (checkboxes)
    if (Array.isArray(value)) {
      return value.join(', ');
    }
    
    // Handle dates
    if (field.key?.includes('date') || field.key?.includes('since') || field.key?.includes('visit')) {
      try {
        const date = new Date(value);
        if (!isNaN(date.getTime())) {
          return date.toLocaleDateString();
        }
      } catch {
        // Not a valid date, continue
      }
    }
    
    // Handle currency
    if (field.key?.includes('spend') || field.key?.includes('amount')) {
      const num = parseFloat(value);
      if (!isNaN(num)) {
        return `$${num.toFixed(2)}`;
      }
    }
    
    return value.toString();
  };

  const saveAndActivateRewards = async () => {
    if (!contactForm.firstName || !contactForm.phone) {
      toast({
        title: 'Required Fields',
        description: 'First name and phone number are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      // Update the contact with new details AND activate rewards
      const updatePayload = {
        firstName: contactForm.firstName,
        lastName: contactForm.lastName,
        phone: contactForm.phone,
        email: contactForm.email,
        address1: contactForm.address1,
        city: contactForm.city,
        state: contactForm.state,
        country: contactForm.country,
        postalCode: contactForm.postalCode
      };
      
      // Only add rewards activation if they're not already a member
      if (selectedContact && !isRewardsMember(selectedContact)) {
        const settings = getRewardsSettings();
        updatePayload.customField = {
          [settings.checkboxFieldId]: settings.checkboxOptionValue.toLowerCase(),
          [settings.dateFieldId]: new Date().toISOString()
        };
      }
      
      console.log('Sending update payload:', updatePayload);
      
      const response = await api.patch(`/live/contacts/${selectedContact.id}`, updatePayload, { 
        headers: { 'X-Use-Live': 'true' } 
      });
      
      console.log('Update response:', response.data);
      
      // Close the modal
      setReviewDialogOpen(false);
      
      // Show success overlay
      setShowSuccessOverlay(true);
      
      // Navigate back to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
      // Clear search and results
      setQuery('');
      setSearchResults([]);
      setSelectedContact(null);
    } catch (error) {
      console.error('Activation failed:', error);
      console.error('Error response:', error.response?.data);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to update contact and activate rewards. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const confirmDisableRewards = async () => {
    if (!selectedContact) return;

    try {
      // Set rewards field to empty array to uncheck it
      const settings = getRewardsSettings();
      const updatePayload = {
        customField: {
          [settings.checkboxFieldId]: [] // Empty array unchecks the checkbox
        }
      };
      
      console.log('Disabling rewards with payload:', updatePayload);
      
      await api.patch(`/live/contacts/${selectedContact.id}`, updatePayload, { 
        headers: { 'X-Use-Live': 'true' } 
      });
      
      toast({
        title: 'Rewards Disabled',
        description: `Rewards membership has been deactivated for ${contactForm.firstName} ${contactForm.lastName}`,
      });
      
      // Close modals and clear
      setShowDisableConfirm(false);
      setReviewDialogOpen(false);
      setQuery('');
      setSearchResults([]);
      setSelectedContact(null);
      
    } catch (error) {
      console.error('Disable failed:', error);
      setShowDisableConfirm(false);
      toast({
        title: 'Error',
        description: error.response?.data?.detail || 'Failed to disable rewards. Please try again.',
        variant: 'destructive'
      });
    }
  };

  const createAndActivateRewards = async () => {
    if (!form.firstName || !form.phone) {
      toast({
        title: 'Required Fields',
        description: 'First name and phone number are required',
        variant: 'destructive'
      });
      return;
    }

    try {
      const settings = getRewardsSettings();
      const contact = {
        firstName: form.firstName,
        lastName: form.lastName,
        phone: form.phone,
        email: form.email,
        address1: form.address1,
        city: form.city,
        state: form.state,
        country: form.country,
        postalCode: form.postalCode,
        tags: ['Rewards Member'],
        customField: {
          [settings.checkboxFieldId]: settings.checkboxOptionValue.toLowerCase(),
          [settings.dateFieldId]: new Date().toISOString()
        }
      };
      
      console.log('Creating new contact:', contact);
      
      await api.post('/live/contacts', contact, { params: { live: 1 } });
      
      // Close the modal
      setCreateDialogOpen(false);
      
      // Show success overlay
      setShowSuccessOverlay(true);
      
      // Navigate back to dashboard after 2 seconds
      setTimeout(() => {
        navigate('/');
      }, 2000);
      
      // Clear everything
      setForm({ 
        firstName: '', 
        lastName: '', 
        phone: '', 
        email: '', 
        address1: '',
        city: '',
        state: '',
        country: '',
        postalCode: ''
      });
      setQuery('');
      setSearchResults([]);
    } catch (error) {
      console.error('========== Creation Error ==========');
      console.error('Full error:', error);
      console.error('Error response:', error.response);
      console.error('Error response data:', error.response?.data);
      console.error('Error response detail:', error.response?.data?.detail);
      console.error('===================================');
      
      // Get error message from backend
      const errorDetail = error.response?.data?.detail || error.response?.data?.message || '';
      console.log('Extracted errorDetail:', errorDetail);
      
      const isDuplicate = errorDetail.toLowerCase().includes('already exists') || 
                          errorDetail.toLowerCase().includes('duplicate') ||
                          errorDetail.toLowerCase().includes('duplicated');
      
      console.log('Is duplicate?', isDuplicate);
      
      if (isDuplicate) {
        // Extract matching field (email or phone)
        let matchingField = 'information';
        if (errorDetail.toLowerCase().includes('email')) {
          matchingField = 'email address';
        } else if (errorDetail.toLowerCase().includes('phone')) {
          matchingField = 'phone number';
        }
        
        console.log('Showing duplicate toast for:', matchingField);
        
        toast({
          title: 'Contact Already Exists',
          description: `A contact with this ${matchingField} already exists. Please search for the existing contact instead.`,
          variant: 'destructive',
          duration: 6000
        });
      } else {
        console.log('Showing generic error toast');
        
        toast({
          title: 'Error Creating Contact',
          description: errorDetail || 'Failed to create contact. Please try again.',
          variant: 'destructive',
          duration: 5000
        });
      }
    }
  };

  return (
    <div className="p-6 max-w-4xl mx-auto space-y-4">
      {/* Search for Contact */}
      <Card>
        <CardHeader>
          <CardTitle>Members</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Search for Customer</Label>
            <Input 
              data-testid="rewards-search-input" 
              placeholder="Type name, phone, or email..." 
              value={query} 
              onChange={(e) => setQuery(e.target.value)}
              className="h-12 text-lg mt-1" 
            />
            {searching && (
              <div className="text-sm text-gray-500 mt-1 flex items-center gap-1">
                <Loader2 className="h-3 w-3 animate-spin" />
                Searching...
              </div>
            )}
          </div>

          {/* Search Results - Top 5 */}
          {searchResults.length > 0 && (
            <div className="border rounded-md max-h-[400px] overflow-y-auto bg-white shadow-sm">
              {searchResults.map((contact) => {
                const isActive = isRewardsMember(contact);
                return (
                  <button
                    key={contact.id}
                    onClick={() => handleContactSelect(contact)}
                    className="w-full text-left p-4 hover:bg-gray-100 border-b last:border-b-0 transition-colors flex justify-between items-center"
                  >
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <span className="font-semibold text-lg">
                          {contact.name || `${contact.firstName || ''} ${contact.lastName || ''}`.trim()}
                        </span>
                        {isActive && (
                          <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                            <BadgeCheck className="h-3 w-3" />
                            Active Member
                          </span>
                        )}
                      </div>
                      <div className="text-sm text-gray-600">{contact.email || 'No email'}</div>
                    </div>
                    {contact.phone && (
                      <div className="text-sm font-medium text-gray-700 ml-4">
                        {contact.phone}
                      </div>
                    )}
                  </button>
                );
              })}
            </div>
          )}

          {/* No results message */}
          {query.length >= 2 && !searching && searchResults.length === 0 && (
            <div className="text-sm text-gray-600 p-3 bg-gray-50 rounded">
              No customers found. Create a new contact below.
            </div>
          )}

          {/* Divider */}
          {(searchResults.length > 0 || (query.length >= 2 && !searching && searchResults.length === 0)) && (
            <div className="relative">
              <div className="absolute inset-0 flex items-center">
                <span className="w-full border-t" />
              </div>
              <div className="relative flex justify-center text-xs uppercase">
                <span className="bg-white px-2 text-muted-foreground">Or</span>
              </div>
            </div>
          )}

          {/* Review & Edit Contact Dialog */}
          <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
            <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto top-[5%] translate-y-0">
              <DialogHeader>
                <DialogTitle>
                  {selectedContact && isRewardsMember(selectedContact) ? 'Review Contact' : 'Review Contact & Activate Rewards'}
                </DialogTitle>
              </DialogHeader>
              <div className="py-2">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                  {/* Left Column - Contact Details */}
                  <div className="lg:col-span-2 space-y-4">
                    {/* Name Fields - 2 columns */}
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="review-firstName">First Name *</Label>
                    <Input
                      id="review-firstName"
                      placeholder="John"
                      value={contactForm.firstName}
                      onChange={(e) => setContactForm({ ...contactForm, firstName: e.target.value })}
                      className="h-12 text-base"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="review-lastName">Last Name</Label>
                    <Input
                      id="review-lastName"
                      placeholder="Doe"
                      value={contactForm.lastName}
                      onChange={(e) => setContactForm({ ...contactForm, lastName: e.target.value })}
                    />
                  </div>
                </div>

                {/* Contact Fields - 2 columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="review-phone">Phone *</Label>
                    <Input
                      id="review-phone"
                      placeholder="+1 (555) 123-4567"
                      value={contactForm.phone}
                      onChange={(e) => setContactForm({ ...contactForm, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="review-email">Email</Label>
                    <Input
                      id="review-email"
                      type="email"
                      placeholder="john@example.com"
                      value={contactForm.email}
                      onChange={(e) => setContactForm({ ...contactForm, email: e.target.value })}
                    />
                  </div>
                </div>
                
                {/* Address Section */}
                <div className="pt-2 border-t">
                  <Label className="text-sm font-semibold mb-3 block">Address (Optional)</Label>
                  <div className="space-y-3">
                    <Input
                      id="review-address1"
                      placeholder="Street Address"
                      value={contactForm.address1}
                      onChange={(e) => setContactForm({ ...contactForm, address1: e.target.value })}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        id="review-city"
                        placeholder="City"
                        value={contactForm.city}
                        onChange={(e) => setContactForm({ ...contactForm, city: e.target.value })}
                      />
                      <Input
                        id="review-state"
                        placeholder="State"
                        value={contactForm.state}
                        onChange={(e) => setContactForm({ ...contactForm, state: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        id="review-postalCode"
                        placeholder="Postal Code"
                        value={contactForm.postalCode}
                        onChange={(e) => setContactForm({ ...contactForm, postalCode: e.target.value })}
                      />
                      <Input
                        id="review-country"
                        placeholder="Country"
                        value={contactForm.country}
                        onChange={(e) => setContactForm({ ...contactForm, country: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                  </div>
                  
                  {/* Right Column - Rewards Status */}
                  <div className="lg:col-span-1">
                    <div className="bg-gray-50 rounded-lg p-4 h-fit">
                      <h3 className="font-semibold text-sm text-gray-700 mb-3">Rewards Status</h3>
                      {selectedContact && isRewardsMember(selectedContact) ? (
                        <div className="space-y-4">
                          <div className="flex items-center gap-2 text-green-700 pb-2 border-b border-gray-200">
                            <BadgeCheck className="h-5 w-5" />
                            <span className="font-semibold">Active Member</span>
                          </div>
                          <div className="space-y-3 mb-4">
                            {loadingFieldDefinitions && (
                              <div className="flex items-center gap-2 text-gray-500 text-sm py-2">
                                <Loader2 className="h-4 w-4 animate-spin" />
                                <span>Loading field names...</span>
                              </div>
                            )}
                            {getRewardsCustomFields(selectedContact).map((field, index) => (
                              <div key={index} className="border-b border-gray-100 pb-2 last:border-0">
                                <div className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-1">
                                  {getFieldLabel(field)}
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                  {formatFieldValue(field)}
                                </div>
                              </div>
                            ))}
                          </div>
                          <Button
                            variant="destructive"
                            size="sm"
                            onClick={() => setShowDisableConfirm(true)}
                            className="w-full h-10"
                          >
                            Disable Rewards
                          </Button>
                        </div>
                      ) : (
                        <div className="text-sm text-gray-600">
                          <p>This contact will become a rewards member when you save.</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
                
                <Button 
                  className="w-full mt-6" 
                  size="lg"
                  onClick={saveAndActivateRewards}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  {selectedContact && isRewardsMember(selectedContact) ? 'Save' : 'Save & Activate Rewards'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>

          {/* Create New Contact Button */}
          <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                data-testid="create-new-contact-button" 
                variant="outline" 
                className="w-full h-14 text-lg" 
                size="lg"
              >
                <UserPlus className="h-5 w-5 mr-2" />
                Create New Contact
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto top-[5%] translate-y-0">
              <DialogHeader>
                <DialogTitle>Create New Contact & Activate Rewards</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 py-2">
                {/* Name Fields - 2 columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <div className="space-y-2">
                    <Label htmlFor="firstName">First Name *</Label>
                    <Input
                      id="firstName"
                      placeholder="John"
                      value={form.firstName}
                      onChange={(e) => setForm({ ...form, firstName: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="lastName">Last Name</Label>
                    <Input
                      id="lastName"
                      placeholder="Doe"
                      value={form.lastName}
                      onChange={(e) => setForm({ ...form, lastName: e.target.value })}
                    />
                  </div>
                </div>

                {/* Contact Fields - 2 columns */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="phone">Phone *</Label>
                    <Input
                      id="phone"
                      placeholder="+1 (555) 123-4567"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="email">Email</Label>
                    <Input
                      id="email"
                      type="email"
                      placeholder="john@example.com"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                    />
                  </div>
                </div>
                
                {/* Address Section */}
                <div className="pt-2 border-t">
                  <Label className="text-sm font-semibold mb-3 block">Address (Optional)</Label>
                  <div className="space-y-3">
                    <Input
                      id="address1"
                      placeholder="Street Address"
                      value={form.address1}
                      onChange={(e) => setForm({ ...form, address1: e.target.value })}
                    />
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        id="city"
                        placeholder="City"
                        value={form.city}
                        onChange={(e) => setForm({ ...form, city: e.target.value })}
                      />
                      <Input
                        id="state"
                        placeholder="State"
                        value={form.state}
                        onChange={(e) => setForm({ ...form, state: e.target.value })}
                      />
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <Input
                        id="postalCode"
                        placeholder="Postal Code"
                        value={form.postalCode}
                        onChange={(e) => setForm({ ...form, postalCode: e.target.value })}
                      />
                      <Input
                        id="country"
                        placeholder="Country"
                        value={form.country}
                        onChange={(e) => setForm({ ...form, country: e.target.value })}
                      />
                    </div>
                  </div>
                </div>
                
                <Button 
                  className="w-full" 
                  size="lg"
                  onClick={createAndActivateRewards}
                >
                  <CheckCircle className="h-4 w-4 mr-2" />
                  Create & Activate Rewards
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </CardContent>
      </Card>

      {/* Disable Rewards Confirmation Dialog */}
      <AlertDialog open={showDisableConfirm} onOpenChange={setShowDisableConfirm}>
        <AlertDialogContent className="top-[20%] translate-y-0">
          <AlertDialogHeader>
            <AlertDialogTitle>Disable Customer Rewards?</AlertDialogTitle>
            <AlertDialogDescription className="text-base py-2">
              This will disable customer rewards and will delete all previous data. Are you sure you want to continue?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="gap-2 sm:gap-0">
            <AlertDialogCancel className="h-12 text-base">No, Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={confirmDisableRewards}
              className="h-12 text-base bg-red-600 hover:bg-red-700"
            >
              Yes, Disable
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Success Overlay */}
      {showSuccessOverlay && (
        <div className="fixed inset-0 bg-green-600 bg-opacity-95 z-50 flex items-center justify-center animate-in fade-in duration-300">
          <div className="text-center text-white">
            <CheckCircle className="h-32 w-32 mx-auto mb-6 animate-in zoom-in duration-500" />
            <h2 className="text-4xl font-bold mb-2">Rewards Activated!</h2>
            <p className="text-xl">Returning to dashboard...</p>
          </div>
        </div>
      )}
    </div>
  );
}
